import type { Locale } from "../i18n/types.ts";
import { SITE_URL } from "./constants.ts";

interface SeoMeta {
  title: string;
  description: string;
}

type SeoPage = "home" | "classic" | "ai" | "aiteam";

const seoData: Record<SeoPage, Record<Locale, SeoMeta>> = {
  home: {
    en: {
      title: "ShootCreator - Basketball Shot Counter & Analytics Platform",
      description:
        "Track, analyze, and perfect every basketball shot. Voice-controlled counting, 19-zone NBA-style court analytics, Apple Watch integration.",
    },
    ja: {
      title: "ShootCreator - バスケットボールシュートカウンター＆分析",
      description:
        "すべてのシュートを記録・分析・改善。音声操作、NBA公式準拠19ゾーンコート解析、Apple Watch連携。",
    },
  },
  classic: {
    en: {
      title: "ShootCreator Classic - Voice-Controlled Basketball Shot Counter",
      description:
        "Hands-free shot counting with voice control. 19-zone analytics, Apple Watch tracking, session history. Free and offline-first.",
    },
    ja: {
      title: "ShootCreator Classic - 音声操作バスケシュートカウンター",
      description:
        "音声操作でハンズフリーのシュートカウント。19ゾーン分析、Apple Watch追跡、セッション履歴。無料＆オフライン対応。",
    },
  },
  ai: {
    en: {
      title: "ShootCreator AI - AI-Powered Basketball Shot Analysis",
      description:
        "AI error classification (Arc, Depth, Direction), heart rate monitoring, voice transcript analysis. Your personal AI shooting coach.",
    },
    ja: {
      title: "ShootCreator AI - AI搭載バスケットボールシュート分析",
      description:
        "AIエラー分類（アーク・深さ・方向）、心拍モニタリング、音声メモ分析。あなた専属のAIシューティングコーチ。",
    },
  },
  aiteam: {
    en: {
      title: "ShootCreator AI Teams - Team Analytics & Load Management",
      description:
        "Real-time biometric monitoring, Polar BLE integration, load management, and team analytics for basketball coaching staff.",
    },
    ja: {
      title: "ShootCreator AI Teams - チーム分析＆負荷管理",
      description:
        "リアルタイムバイオメトリクス、Polar BLE連携、負荷管理、バスケットボールコーチ向けチーム分析。",
    },
  },
};

export function getSeoMeta(page: SeoPage, locale: Locale) {
  return seoData[page][locale];
}

/** Build canonical URL for a given path */
export function getCanonical(path: string, locale: Locale) {
  return locale === "ja" ? `${SITE_URL}/ja${path}` : `${SITE_URL}${path}`;
}

/** Build hreflang alternate links for a path */
export function getHreflangs(path: string) {
  return {
    en: `${SITE_URL}${path}`,
    ja: `${SITE_URL}/ja${path}`,
  };
}

export function buildJsonLdWebSite() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ShootCreator",
    url: SITE_URL,
  };
}

export function buildJsonLdSoftwareApp(options: {
  name: string;
  description: string;
  price: string;
  priceCurrency: string;
  operatingSystem: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: options.name,
    description: options.description,
    applicationCategory: "SportsApplication",
    operatingSystem: options.operatingSystem,
    offers: {
      "@type": "Offer",
      price: options.price,
      priceCurrency: options.priceCurrency,
    },
  };
}

/**
 * @deprecated Use getSeoMeta + getCanonical instead
 */
export const SEO: Record<
  string,
  { title: string; description: string; canonical: string; ogImage?: string }
> = {
  home: {
    ...seoData.home.en,
    canonical: `${SITE_URL}/basketball/classic`,
    ogImage: "/og-classic.png",
  },
  classic: {
    ...seoData.classic.en,
    canonical: `${SITE_URL}/basketball/classic`,
    ogImage: "/og-classic.png",
  },
  ai: {
    ...seoData.ai.en,
    canonical: `${SITE_URL}/basketball/ai`,
    ogImage: "/og-ai.png",
  },
};
